import React, { useState, useEffect, useRef } from 'react'
import { useZxing } from 'react-zxing'
import { BarcodeFormat, DecodeHintType } from '@zxing/library'

const CONSTRAINTS = {
  video: { facingMode: 'environment' },
  audio: false,
}

const TIMEOUT = 15000 // 15 seconds
const ERROR_TIMEOUT_MESSAGE = 'Scan timeout. Please ensure the barcode is clearly visible and try again.'

const BarcodeScanner = ({ onScan, inputComponent }) => {
  const [scanning, setScanning] = useState(false)
  const [deviceId, setDeviceId] = useState(null)
  const [result, setResult] = useState('')
  const [cameraError, setCameraError] = useState(null)
  const [timeoutCount, setTimeoutCount] = useState(0) // New state to count timeouts

  const videoElRef = useRef(null) // holds the actual <video> element
  const startingScanRef = useRef(false) // guard against double getUserMedia calls
  const lastDecodedRef = useRef({ text: '', ts: 0 }) // dedupe quick repeats

  // Start scanning on user gesture
  const handleStartScanning = () => {
    if (startingScanRef.current) return
    startingScanRef.current = true

    setResult('')
    setTimeoutCount(0)
    setScanning(true)
    navigator.mediaDevices
      .getUserMedia(CONSTRAINTS)
      .then(() => navigator.mediaDevices.enumerateDevices())
      .then(devices => {
        const cameras = devices.filter(device => device.kind === 'videoinput')
        if (cameras.length > 0) {
          const backCamera = cameras.find(device => device.label.toLowerCase().includes('back')) || cameras[0]
          setDeviceId(backCamera.deviceId)
          startingScanRef.current = false
        } else {
          setCameraError('Cannot access camera, please try again on a different device.')
          startingScanRef.current = false
        }
      })
      .catch(error => {
        setCameraError('Cannot access camera, please try again on a different device.')
        startingScanRef.current = false
      })
  }

  const stopStream = () => {
    try {
      const el = videoElRef.current
      const stream = el && el.srcObject
      if (stream) {
        stream.getTracks().forEach(t => t.stop())
      }
    } catch (_) {
      // no-op
    }
  }

  const onClose = () => {
    stopStream()
    setScanning(false)
  }

  const barcodeOnlyHints = new Map()
  barcodeOnlyHints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_128, BarcodeFormat.CODE_39])
  // barcodeOnlyHints.set(DecodeHintType.TRY_HARDER, true)

  const { ref } = useZxing({
    paused: !deviceId || !scanning || result,
    deviceId,
    constraints: CONSTRAINTS,
    onError: error => {
      setCameraError(ERROR_TIMEOUT_MESSAGE)
    },
    onDecodeResult: res => {
      const text = res?.getText?.()
      if (!text) return
      const now = Date.now()
      const { text: lastText, ts } = lastDecodedRef.current
      // Ignore if the same text was decoded within the last 1000ms
      if (text === lastText && now - ts < 1000) return
      lastDecodedRef.current = { text, ts: now }

      setResult(text)
      if (onScan) onScan(text)
      onClose()
    },
    timeBetweenDecodingAttempts: 200,
    hints: barcodeOnlyHints,
  })

  useEffect(() => {
    // Mirror the hook ref to a local ref so we can stop the stream on close
    const el = typeof ref === 'function' ? null : ref.current
    if (el && el !== videoElRef.current) {
      videoElRef.current = el
    }
  }, [ref])

  useEffect(() => {
    if (!scanning || result) return

    const timeout = setTimeout(() => {
      if (!result) {
        setCameraError(ERROR_TIMEOUT_MESSAGE)
        setTimeoutCount(prevCount => {
          const newCount = prevCount + 1
          if (newCount >= 3) {
            setScanning(false)
          }
          return newCount
        })
      }
    }, TIMEOUT)

    return () => clearTimeout(timeout)
  }, [scanning, result])

  // Reset everything
  const handleReset = () => {
    stopStream()
    setScanning(false)
    setResult('')
    setDeviceId(null)
    setCameraError(null)
  }

  useEffect(() => {
    return () => {
      stopStream()
    }
  }, [])

  return (
    <div>
      {/* Top row: Scan button and input component */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {timeoutCount <= 3 && (
          <button
            type="button"
            onClick={handleStartScanning}
            className="registerKitFieldsNew"
            style={{
              width: '200px',
              background: '#ee8155',
              color: 'white',
              fontSize: '14px',
              lineHeight: '14px',
              border: 'none',
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.2)',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <img src="/img/barcode.png" alt="barcode" style={{ width: '28px', height: '28px' }} />
            Scan Barcode
          </button>
        )}
        {timeoutCount <= 3 && (
          <div
            style={{
              height: '50px',
              fontSize: '14px',
              textAlign: 'center',
              color: '#1F7F61',
              padding: '0 8px',
            }}
          >
            or
          </div>
        )}
        {inputComponent}
      </div>

      {/* Modal for camera view */}
      {scanning && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
        >
          {/* Close button */}
          <div
            onClick={handleReset}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              color: '#fff',
              fontSize: '48px',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            &times;
          </div>

          {/* Instructional text */}
          {!cameraError && (
            <div
              style={{
                position: 'absolute',
                top: '12%',
                color: '#fff',
                fontSize: '18px',
                textAlign: 'center',
              }}
            >
              <p>Point the camera at your kit's barcode.</p>
              <p>Ensure the barcode is fully visible and not too close.</p>
            </div>
          )}

          {/* Loading info */}
          {!cameraError && !result && <div style={{ color: 'white', marginBottom: '10px', marginTop: '100px' }}>Looking for barcode...</div>}

          {/* Video container */}
          <div
            style={{
              position: 'relative',
              width: '90%',
              maxWidth: '600px',
              height: '60%',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            {cameraError ? (
              <div style={{ color: '#fff', fontSize: '18px', textAlign: 'center' }}>{cameraError}</div>
            ) : (
              <>
                <div aria-live="polite" style={{ position: 'absolute', top: '8px', left: '8px', fontSize: '12px', color: '#fff', opacity: 0.7 }}>
                  Scanning…
                </div>
                <video ref={ref} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '3px',
                      background: '#1F7F61',
                      animation: 'scan 2s linear infinite',
                    }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Scanner animation */}
          <style jsx>{`
            @keyframes scan {
              0% {
                top: 0;
              }
              50% {
                top: 50%;
              }
              100% {
                top: 0;
              }
            }
          `}</style>
        </div>
      )}
    </div>
  )
}

export default BarcodeScanner
